/**
 * API utility for making authenticated requests to the backend
 * Handles JWT tokens, request/response formatting, and error handling
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Token storage keys
const TOKEN_KEY = 'healthcues_token';

/**
 * Get the stored JWT token
 */
export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store the JWT token
 */
export function setToken(token) {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
}

/**
 * Clear the JWT token (logout)
 */
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // Get token and add to headers
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, config);
    
    // Handle 401 Unauthorized - but NOT for login/first-login endpoints
    // (those endpoints return 401 for invalid credentials, not expired sessions)
    const isAuthEndpoint = endpoint === '/api/auth/login' || endpoint === '/api/auth/first-login';
    
    if (response.status === 401 && !isAuthEndpoint) {
      clearToken();
      window.location.href = '/'; // Redirect to login
      throw new Error('Session expired. Please log in again.');
    }
    
    // Parse response
    const data = await response.json();
    
    // Handle error responses
    if (!response.ok) {
      // For validation errors (422), include more detail
      if (response.status === 422 && data.detail) {
        const errorMsg = typeof data.detail === 'string' 
          ? data.detail 
          : JSON.stringify(data.detail);
        console.error('Validation error (422):', errorMsg);
        throw new Error(`Validation error: ${errorMsg}`);
      }
      throw new Error(data.detail || data.message || 'Request failed');
    }
    
    return data;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}

/**
 * API methods
 */
export const api = {
  // Generic HTTP methods
  get: async (endpoint) => {
    return apiRequest(endpoint);
  },
  
  post: async (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  
  put: async (endpoint, data = {}) => {
    return apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  
  delete: async (endpoint) => {
    return apiRequest(endpoint, {
      method: 'DELETE',
    });
  },
  
  // Authentication
  auth: {
    login: async (email, password) => {
      return apiRequest('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
    },
    
    firstLogin: async (userId, newPassword, isEmployerContact = false, clientId = null) => {
      return apiRequest('/api/auth/first-login', {
        method: 'POST',
        body: JSON.stringify({
          userId,
          newPassword,
          isEmployerContact,
          clientId,
        }),
      });
    },
    
    me: async () => {
      return apiRequest('/api/auth/me');
    },
    
    changePassword: async (newPassword) => {
      return apiRequest('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ newPassword }),
      });
    },
  },
  
  // Clients
  clients: {
    list: async () => {
      return apiRequest('/api/clients');
    },
    
    get: async (clientId) => {
      return apiRequest(`/api/clients/${clientId}`);
    },
    
    create: async (clientData) => {
      return apiRequest('/api/clients', {
        method: 'POST',
        body: JSON.stringify(clientData),
      });
    },
    
    update: async (clientId, clientData) => {
      return apiRequest(`/api/clients/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify(clientData),
      });
    },

    updateEmployee: async (clientId, employeeId, fields) => {
      return apiRequest(`/api/clients/${clientId}/employees/${employeeId}`, {
        method: 'PATCH',
        body: JSON.stringify(fields),
      });
    },

    delete: async (clientId) => {
      return apiRequest(`/api/clients/${clientId}`, {
        method: 'DELETE',
      });
    },
    
    enablePortalAccess: async (clientId, contactId) => {
      return apiRequest(`/api/clients/${clientId}/contacts/${contactId}/enable-portal`, {
        method: 'POST',
      });
    },
    
    disablePortalAccess: async (clientId, contactId) => {
      return apiRequest(`/api/clients/${clientId}/contacts/${contactId}/disable-portal`, {
        method: 'POST',
      });
    },
    
    resetContactPassword: async (clientId, contactId) => {
      return apiRequest(`/api/clients/${clientId}/contacts/${contactId}/reset-password`, {
        method: 'POST',
      });
    },
    
    getContracts: async (clientId) => {
      return apiRequest(`/api/clients/${clientId}/contracts`);
    },
    
    assignContracts: async (clientId, templateIds, assignmentType = 'manual') => {
      return apiRequest(`/api/clients/${clientId}/contracts/assign?assignment_type=${assignmentType}`, {
        method: 'POST',
        body: JSON.stringify(templateIds),
      });
    },
    
    connectBankPlaid: async (clientId, data) => {
      return apiRequest(`/api/clients/${clientId}/banking/connect-plaid`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    
    setupManualACH: async (clientId, data) => {
      return apiRequest(`/api/clients/${clientId}/banking/manual`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    
    uploadCensus: async (clientId, data) => {
      return apiRequest(`/api/clients/${clientId}/census/upload`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    
    setupPayroll: async (clientId, data) => {
      return apiRequest(`/api/clients/${clientId}/payroll/setup`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
  },
  
  // Users
  users: {
    list: async () => {
      return apiRequest('/api/users');
    },
    
    invite: async (userData) => {
      return apiRequest('/api/users/invite', {
        method: 'POST',
        body: JSON.stringify(userData),
      });
    },
    
    update: async (userId, userData) => {
      return apiRequest(`/api/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData),
      });
    },
    
    deactivate: async (userId) => {
      return apiRequest(`/api/users/${userId}/deactivate`, {
        method: 'POST',
      });
    },
    
    reactivate: async (userId) => {
      return apiRequest(`/api/users/${userId}/reactivate`, {
        method: 'POST',
      });
    },
    
    resetPassword: async (userId) => {
      return apiRequest(`/api/users/${userId}/reset-password`, {
        method: 'POST',
      });
    },
  },
  
  // Config
  config: {
    get: async () => {
      return apiRequest('/api/config');
    },
    
    update: async (configData) => {
      return apiRequest('/api/config', {
        method: 'PUT',
        body: JSON.stringify(configData),
      });
    },
    
    getContractTemplates: async () => {
      return apiRequest('/api/config/contract-templates');
    },
    
    createContractTemplate: async (template) => {
      return apiRequest('/api/config/contract-templates', {
        method: 'POST',
        body: JSON.stringify(template),
      });
    },
    
    updateContractTemplate: async (templateId, template) => {
      return apiRequest(`/api/config/contract-templates/${templateId}`, {
        method: 'PUT',
        body: JSON.stringify(template),
      });
    },
    
    deleteContractTemplate: async (templateId) => {
      return apiRequest(`/api/config/contract-templates/${templateId}`, {
        method: 'DELETE',
      });
    },
  },

  // Client Messages (Employer Portal ↔ Internal Team)
  messages: {
    list: async (clientId) => apiRequest(`/api/clients/${clientId}/messages`),
    send: async (clientId, body, recipientId = null) => apiRequest(`/api/clients/${clientId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body, recipientId }),
    }),
    markRead: async (clientId, messageIds) => apiRequest(`/api/clients/${clientId}/messages/mark-read`, {
      method: 'PATCH',
      body: JSON.stringify({ messageIds: messageIds || null }),
    }),
    unreadCounts: async () => apiRequest('/api/messages/unread-counts'),
  },

  // Internal Dashboard
  dashboard: {
    messagesSummary: async () => apiRequest('/api/dashboard/messages-summary'),
    defaultAccountManager: async () => apiRequest('/api/dashboard/default-account-manager'),
    assignedAccountManager: async (clientId) => apiRequest(`/api/clients/${clientId}/assigned-account-manager`),
  },

  // E-Learning (Phase 18)
  learning: {
    dashboardStats: async () => apiRequest('/api/learning/dashboard-stats'),
  },

  // Health check
  health: async () => {
    return apiRequest('/api/health');
  },
};

export default api;
